import { useEffect, useState } from "react";
import type * as React from "react";
import ArrowForward from "@mui/icons-material/ArrowForward";
import { Box, Divider, Typography } from "@mui/material";
import { Link as RouterLink, useHistory } from "react-router-dom";
import DnaScreeningsIcon from "assets/images/DNAScreenings.svg";
import CancerNavigationIcon from "assets/images/CancerNavigation.svg";
import GiftMembershipStepAccessIcon from "assets/images/gift-membership-step-access.svg";
import GiftMembershipStepEnvelopeIcon from "assets/images/gift-membership-step-envelope.svg";
import GiftMembershipStepPencilIcon from "assets/images/gift-membership-step-pencil.svg";
import GiftHowItWorksArrowhead from "assets/images/gift-how-it-works-arrowhead.svg";
import SupportMapIcon from "assets/images/SupportMap.svg";
import {
  formatUsdWithSpaceAfterSymbol,
  useAnimatedUsdString
} from "components/v3/sharedComponents/AnimatedUsdAmount";
import GLButton from "components/v3/sharedComponents/GLButton";
import GLQuantityStepper from "components/v3/sharedComponents/GLQuantityStepper";
import Video from "components/v3/sharedComponents/Video";
import GenomicLifeLegalDisclaimer from "components/v3/sharedComponents/GenomicLifeLegalDisclaimer";
import GradientFramedLayout from "components/v3/layouts/GradientFramedLayout";
import { COLORS } from "components/v3/Theme/colors";
import {
  GENOMIC_LIFE_GIFT_GIVER_VIDEO_URL,
  GENOMIC_LIFE_INVITE_DETAILS_LINK_LABEL,
  GENOMIC_LIFE_INVITE_DETAILS_URL
} from "constants/externalPathConstants";
import {
  GENOMIC_LIFE_COMPLETE_GIFT_RECIPIENT_FEATURES,
  GENOMIC_LIFE_COMPLETE_GIFT_RECIPIENT_INTRO
} from "constants/genomicLifeCompleteProgramCopy";
import {
  GIFT_MAX_PLAN_QUANTITY,
  GIFT_UNIT_PRICE_USD
} from "constants/membershipPriceConstants";
import { PATHS } from "constants/pathConstants";
import { trackGiftMembershipWorkflowStarted } from "analytics";
import PageTitle from "utils/PageTitle";

const HOW_IT_WORKS_CIRCLE_PX = 56;

const howItWorksStepConnectorSx = {
  alignItems: "center",
  display: "flex",
  flex: 1,
  height: HOW_IT_WORKS_CIRCLE_PX,
  minWidth: 0
} as const;

const howItWorksDesktopThreeColGridSx = {
  display: { md: "grid", xs: "none" },
  gap: 2,
  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  width: "100%"
} as const;

const howItWorksStepCircleSx = {
  alignItems: "center",
  bgcolor: COLORS.LIGHT_GRAY,
  borderRadius: "50%",
  display: "flex",
  flexShrink: 0,
  height: HOW_IT_WORKS_CIRCLE_PX,
  justifyContent: "center",
  width: HOW_IT_WORKS_CIRCLE_PX
} as const;

const GIFT_BENEFIT_ART_WIDTH_SM = 180;

const giftBenefitsListWrapperSx = {
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  gap: { sm: 4, xs: 3 },
  maxWidth: 620,
  mx: "auto",
  px: { md: 4, sm: 3, xs: 2 },
  width: "100%"
} as const;

const giftBenefitArtSx = {
  display: { sm: "block", xs: "none" },
  flexShrink: 0,
  height: "auto",
  maxWidth: GIFT_BENEFIT_ART_WIDTH_SM,
  padding: { sm: "0 20px", xs: 0 },
  width: GIFT_BENEFIT_ART_WIDTH_SM
} as const;

const giftBenefitTextOffsetSx = {
  flex: 1,
  minWidth: 0,
  pt: { sm: 5, xs: 0 },
  width: { sm: "auto", xs: "100%" }
} as const;

const giftBenefitRowSx = {
  alignItems: "flex-start",
  display: "flex",
  flexDirection: { sm: "row", xs: "column" },
  gap: 2
} as const;

const giftBenefitRowReverseSx = {
  alignItems: "flex-start",
  display: "flex",
  flexDirection: { sm: "row-reverse", xs: "column" },
  gap: 2
} as const;

type GiftBenefitRowProps = {
  alt?: string;
  artOffsetY?: number;
  children: React.ReactNode;
  reverse?: boolean;
  src: string;
};

const GiftBenefitRow: React.FC<GiftBenefitRowProps> = ({
  alt = "",
  artOffsetY = 0,
  children,
  reverse,
  src
}) => (
  <Box sx={reverse ? giftBenefitRowReverseSx : giftBenefitRowSx}>
    <Box
      alt={alt}
      component="img"
      src={src}
      sx={{
        ...giftBenefitArtSx,
        ...(artOffsetY ? { marginTop: { sm: `${artOffsetY}px`, xs: 0 } } : {})
      }}
    />
    <Box sx={giftBenefitTextOffsetSx}>{children}</Box>
  </Box>
);

const GiftMembershipPage: React.FC = () => {
  const history = useHistory();
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  PageTitle("Gift a membership");

  const yearlyAdditionalUsd = quantity * GIFT_UNIT_PRICE_USD;
  const animatedYearlyLabel = useAnimatedUsdString(yearlyAdditionalUsd);

  const handleContinue = () => {
    trackGiftMembershipWorkflowStarted({ giftQuantity: quantity });
    history.push({
      pathname: PATHS.ACCOUNT_MANAGEMENT_GIFT_RECIPIENTS,
      state: { giftQuantity: quantity }
    });
  };

  const featureBodyProps = {
    component: "p" as const,
    variant: "body" as const,
    sx: { color: COLORS.DARK_GRAY_2, lineHeight: 1.65, m: 0, textAlign: "left" }
  };

  return (
    <GradientFramedLayout showHeader>
      <Box
        sx={{
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          gap: 3,
          marginX: "auto",
          maxWidth: { lg: 1200, xs: "100%" },
          minWidth: 0,
          pb: 5,
          pt: { md: 3, xs: 2 },
          px: { md: 3, xs: 2 },
          width: "100%"
        }}
      >
        <Typography
          component="h1"
          textAlign="center"
          variant="h4_primary"
          sx={{ width: "100%", mt: 6 }}
        >
          Gift A Membership
        </Typography>
        <Typography
          component="p"
          textAlign="center"
          variant="body_lg"
          sx={{
            color: COLORS.DARK_GRAY_2,
            fontStyle: "italic",
            maxWidth: 700,
            mx: "auto",
            mb: 6
          }}
        >
          Buying a membership for someone else? Here&apos;s what you need to
          know
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: { lg: "row", xs: "column" },
            gap: 4,
            alignItems: "flex-start"
          }}
        >
          <Box
            sx={{
              boxSizing: "border-box",
              display: "flex",
              flex: 1,
              flexDirection: "column",
              gap: 3,
              maxWidth: "100%",
              minWidth: 0,
              order: { lg: 1, xs: 2 },
              textAlign: "left",
              width: "100%"
            }}
          >
            <Box
              sx={{
                borderRadius: "12px",
                boxShadow:
                  "0px 10px 15px -3px rgba(0, 0, 0, 0.08), 0px 4px 6px -2px rgba(0, 0, 0, 0.04)",
                overflow: "hidden",
                maxWidth: "100%"
              }}
            >
              <Video
                controls
                height="100%"
                muted
                playing={false}
                style={{
                  aspectRatio: "16/9",
                  height: "100%",
                  width: "100%"
                }}
                width="100%"
                url={GENOMIC_LIFE_GIFT_GIVER_VIDEO_URL}
              />
            </Box>

            <Typography component="h5" variant="body_strong_xl">
              They will receive the same great benefit as you
            </Typography>
            <Typography {...featureBodyProps}>
              {GENOMIC_LIFE_COMPLETE_GIFT_RECIPIENT_INTRO}
            </Typography>

            <Box sx={giftBenefitsListWrapperSx}>
              <GiftBenefitRow src={DnaScreeningsIcon}>
                <Typography {...featureBodyProps}>
                  <Box component="span" sx={{ fontWeight: 700 }}>
                    {GENOMIC_LIFE_COMPLETE_GIFT_RECIPIENT_FEATURES[0].label}
                  </Box>{" "}
                  {GENOMIC_LIFE_COMPLETE_GIFT_RECIPIENT_FEATURES[0].body}
                </Typography>
              </GiftBenefitRow>

              <GiftBenefitRow
                reverse
                artOffsetY={-10}
                src={CancerNavigationIcon}
              >
                <Typography {...featureBodyProps}>
                  <Box component="span" sx={{ fontWeight: 700 }}>
                    {GENOMIC_LIFE_COMPLETE_GIFT_RECIPIENT_FEATURES[1].label}
                  </Box>{" "}
                  {GENOMIC_LIFE_COMPLETE_GIFT_RECIPIENT_FEATURES[1].body}
                </Typography>
              </GiftBenefitRow>

              <GiftBenefitRow artOffsetY={20} src={SupportMapIcon}>
                <Typography {...featureBodyProps}>
                  <Box component="span" sx={{ fontWeight: 700 }}>
                    {GENOMIC_LIFE_COMPLETE_GIFT_RECIPIENT_FEATURES[2].label}
                  </Box>{" "}
                  {GENOMIC_LIFE_COMPLETE_GIFT_RECIPIENT_FEATURES[2].body}
                </Typography>
              </GiftBenefitRow>
            </Box>

            <Box
              sx={{
                display: { sm: "flex", xs: "none" },
                flexDirection: "column",
                gap: 2,
                mt: { lg: 4, sm: 3 },
                textAlign: "left",
                width: "100%"
              }}
            >
              <Typography component="h2" variant="h4_black">
                How it works
              </Typography>

              <Box sx={{ ...howItWorksDesktopThreeColGridSx, mt: 1 }}>
                <Box
                  sx={{
                    alignItems: "center",
                    columnGap: 1.5,
                    display: "flex",
                    minWidth: 0
                  }}
                >
                  <Box sx={howItWorksStepCircleSx}>
                    <Box
                      alt=""
                      component="img"
                      height={24}
                      src={GiftMembershipStepEnvelopeIcon}
                      width={24}
                    />
                  </Box>
                  <Box aria-hidden sx={howItWorksStepConnectorSx}>
                    <Box
                      sx={{
                        bgcolor: COLORS.PRIMARY_40,
                        borderRadius: "2px",
                        flex: 1,
                        height: 2,
                        minWidth: 4
                      }}
                    />
                    <Box
                      alt=""
                      component="img"
                      src={GiftHowItWorksArrowhead}
                      sx={{
                        display: "block",
                        flexShrink: 0,
                        height: 12,
                        marginLeft: "-2px",
                        width: "auto"
                      }}
                    />
                  </Box>
                </Box>
                <Box
                  sx={{
                    alignItems: "center",
                    columnGap: 1.5,
                    display: "flex",
                    minWidth: 0
                  }}
                >
                  <Box sx={howItWorksStepCircleSx}>
                    <Box
                      alt=""
                      component="img"
                      height={24}
                      src={GiftMembershipStepPencilIcon}
                      width={24}
                    />
                  </Box>
                  <Box aria-hidden sx={howItWorksStepConnectorSx}>
                    <Box
                      sx={{
                        bgcolor: COLORS.PRIMARY_40,
                        borderRadius: "2px",
                        flex: 1,
                        height: 2,
                        minWidth: 4
                      }}
                    />
                    <Box
                      alt=""
                      component="img"
                      src={GiftHowItWorksArrowhead}
                      sx={{
                        display: "block",
                        flexShrink: 0,
                        height: 12,
                        marginLeft: "-2px",
                        width: "auto"
                      }}
                    />
                  </Box>
                </Box>
                <Box
                  sx={{ alignItems: "center", display: "flex", minWidth: 0 }}
                >
                  <Box sx={howItWorksStepCircleSx}>
                    <Box
                      alt=""
                      component="img"
                      height={24}
                      src={GiftMembershipStepAccessIcon}
                      width={24}
                    />
                  </Box>
                </Box>
              </Box>

              <Box sx={{ ...howItWorksDesktopThreeColGridSx, mt: 2 }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography component="h3" variant="body_strong_black">
                    Buy a membership
                  </Typography>
                  <Typography
                    component="p"
                    sx={{ color: COLORS.DARK_GRAY, mt: 1 }}
                    variant="body2"
                  >
                    Purchase a 1-year membership for your loved ones.
                  </Typography>
                  <Typography
                    component="p"
                    sx={{
                      color: COLORS.DARK_GRAY,
                      fontStyle: "italic",
                      mt: 1.5
                    }}
                    variant="body2"
                  >
                    After the first year, they can renew annually.
                  </Typography>
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography component="h3" variant="body_strong_black">
                    Send them an invite
                  </Typography>
                  <Typography
                    component="p"
                    sx={{ color: COLORS.DARK_GRAY, mt: 1 }}
                    variant="body2"
                  >
                    Simply share the new member’s email address. We’ll reach
                    out.
                  </Typography>
                  <Typography
                    component="p"
                    sx={{
                      color: COLORS.DARK_GRAY,
                      fontStyle: "italic",
                      mt: 1.5
                    }}
                    variant="body2"
                  >
                    New members can also go directly to{" "}
                    <Box
                      component="a"
                      href={GENOMIC_LIFE_INVITE_DETAILS_URL}
                      rel="noopener noreferrer"
                      sx={{
                        color: "inherit",
                        fontStyle: "italic",
                        fontWeight: 700,
                        textDecoration: "none"
                      }}
                      target="_blank"
                    >
                      {GENOMIC_LIFE_INVITE_DETAILS_LINK_LABEL}
                    </Box>{" "}
                    to get started.
                  </Typography>
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography component="h3" variant="body_strong_black">
                    They're in!
                  </Typography>
                  <Typography
                    component="p"
                    sx={{ color: COLORS.DARK_GRAY, mt: 1 }}
                    variant="body2"
                  >
                    After completing the sign-up process, new members have full
                    access to the program.
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  display: { md: "none", xs: "flex" },
                  flexDirection: "column",
                  gap: 3,
                  mt: 1
                }}
              >
                <Box>
                  <Box sx={howItWorksStepCircleSx}>
                    <Box
                      alt=""
                      component="img"
                      height={24}
                      src={GiftMembershipStepEnvelopeIcon}
                      width={24}
                    />
                  </Box>
                  <Typography
                    component="h3"
                    sx={{ mt: 2 }}
                    variant="body_strong_black"
                  >
                    Buy a membership
                  </Typography>
                  <Typography
                    component="p"
                    sx={{ color: COLORS.DARK_GRAY_2, mt: 1 }}
                    variant="body2"
                  >
                    Purchase additional memberships to your plan. There are no
                    limits.
                  </Typography>
                  <Typography
                    component="p"
                    sx={{
                      color: COLORS.DARK_GRAY,
                      fontStyle: "italic",
                      mt: 1.5
                    }}
                    variant="body2"
                  >
                    This requires an immediate credit card authorization and
                    payment and their membership will renew yearly.
                  </Typography>
                </Box>
                <Box>
                  <Box sx={howItWorksStepCircleSx}>
                    <Box
                      alt=""
                      component="img"
                      height={24}
                      src={GiftMembershipStepPencilIcon}
                      width={24}
                    />
                  </Box>
                  <Typography
                    component="h3"
                    sx={{ mt: 2 }}
                    variant="body_strong_black"
                  >
                    Send them an invite
                  </Typography>
                  <Typography
                    component="p"
                    sx={{ color: COLORS.DARK_GRAY_2, mt: 1 }}
                    variant="body2"
                  >
                    Tell us who they&apos;re for and where to send their code
                    to.
                  </Typography>
                  <Typography
                    component="p"
                    sx={{
                      color: COLORS.DARK_GRAY,
                      fontStyle: "italic",
                      mt: 1.5
                    }}
                    variant="body2"
                  >
                    They can also go directly to{" "}
                    <Box
                      component="a"
                      href={GENOMIC_LIFE_INVITE_DETAILS_URL}
                      rel="noopener noreferrer"
                      sx={{
                        color: "inherit",
                        fontStyle: "italic",
                        fontWeight: 700,
                        textDecoration: "none"
                      }}
                      target="_blank"
                    >
                      {GENOMIC_LIFE_INVITE_DETAILS_LINK_LABEL}
                    </Box>{" "}
                    to get started.
                  </Typography>
                </Box>
                <Box>
                  <Box sx={howItWorksStepCircleSx}>
                    <Box
                      alt=""
                      component="img"
                      height={24}
                      src={GiftMembershipStepAccessIcon}
                      width={24}
                    />
                  </Box>
                  <Typography
                    component="h3"
                    sx={{ mt: 2 }}
                    variant="body_strong_black"
                  >
                    They get to access!
                  </Typography>
                  <Typography
                    component="p"
                    sx={{ color: COLORS.DARK_GRAY_2, mt: 1 }}
                    variant="body2"
                  >
                    Once they complete the sign up process, they&apos;ll get
                    full access to Genomic Life, just like you!
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              bgcolor: COLORS.BACKGROUND_BLUE,
              borderRadius: "12px",
              boxSizing: "border-box",
              order: { lg: 2, xs: 1 },
              p: 4,
              width: { lg: 380, xs: "100%" }
            }}
          >
            <Typography
              component="h2"
              variant="body_strong_lg_black"
              sx={{ mb: 3 }}
            >
              How many plans would you like to purchase?
            </Typography>

            <Box
              sx={{
                alignItems: "center",
                display: "flex",
                flexDirection: "row",
                flexWrap: "nowrap",
                gap: 2
              }}
            >
              <GLQuantityStepper
                decrementAriaLabel="Decrease number of plans"
                incrementAriaLabel="Increase number of plans"
                max={GIFT_MAX_PLAN_QUANTITY}
                min={1}
                onChange={setQuantity}
                value={quantity}
              />
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  minWidth: 0
                }}
              >
                <Typography
                  sx={{
                    color: COLORS.DARK_GRAY_2,
                    fontWeight: 700,
                    lineHeight: 1.3
                  }}
                  variant="body_strong_black"
                >
                  x {formatUsdWithSpaceAfterSymbol(GIFT_UNIT_PRICE_USD)}
                </Typography>
                <Typography
                  sx={{
                    color: COLORS.DARK_GRAY,
                    fontStyle: "italic",
                    lineHeight: 1.35,
                    mt: 0.25
                  }}
                  variant="body2"
                >
                  Price per membership
                </Typography>
              </Box>
            </Box>

            <Typography
              sx={{
                color:
                  quantity >= GIFT_MAX_PLAN_QUANTITY
                    ? COLORS.NOTICE
                    : COLORS.DARK_GRAY,
                fontStyle: "italic",
                mt: 1
              }}
              variant="body2"
            >
              Maximum of {GIFT_MAX_PLAN_QUANTITY}.
            </Typography>

            <Divider
              sx={{
                borderBottomWidth: "3px",
                borderColor: COLORS.PRIMARY_LIGHTEST,
                my: 4
              }}
            />

            <Typography sx={{ mb: 0.5 }} variant="body_black">
              Your yearly additional cost
            </Typography>
            <Typography
              sx={{ fontVariantNumeric: "tabular-nums", mb: 2 }}
              variant="body_strong_lg_accent2"
            >
              {animatedYearlyLabel}
            </Typography>

            <GLButton
              buttonType="primary"
              onClick={handleContinue}
              rightIcon={<ArrowForward />}
              sx={{ mb: 3 }}
              text="Continue"
            />

            <Typography
              component="p"
              sx={{ mb: 2, lineHeight: 1.65 }}
              variant="body_dark_gray"
            >
              → First-year membership included
              <br />
              → Gift recipients have the opportunity to renew after 1 year
              <br />→ Gift Memberships are subject to our refund and
              cancellation policy
            </Typography>
            <Typography component="p" sx={{ m: 0 }} variant="body_dark_gray">
              Please see our{" "}
              <Box
                component={RouterLink}
                rel="noopener noreferrer"
                target="_blank"
                to={PATHS.ACCOUNT_MANAGEMENT_TERMS_AND_CONDITIONS}
                sx={{
                  color: "inherit",
                  textDecoration: "underline",
                  textUnderlineOffset: "2px"
                }}
              >
                Terms of Use
              </Box>{" "}
              and{" "}
              <Box
                component={RouterLink}
                rel="noopener noreferrer"
                target="_blank"
                to={PATHS.ACCOUNT_MANAGEMENT_PRIVACY_POLICY}
                sx={{
                  color: "inherit",
                  textDecoration: "underline",
                  textUnderlineOffset: "2px"
                }}
              >
                Privacy Policy
              </Box>{" "}
              for details.
            </Typography>
          </Box>
        </Box>
        <Divider
          sx={{
            borderBottomWidth: "3px",
            borderColor: COLORS.PRIMARY_LIGHTEST,
            my: { sm: 4, xs: 2 }
          }}
        />

        <GenomicLifeLegalDisclaimer
          gap={3}
          textSx={{ color: COLORS.DARK_GRAY, m: 0, mt: { sm: 0, xs: 3 } }}
          typographyVariant="body"
        />
      </Box>
    </GradientFramedLayout>
  );
};

export default GiftMembershipPage;
